import React, { forwardRef, useEffect } from "react";
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormReturn,
  DefaultValues,
} from "react-hook-form";

type FormProps<TFormValues extends FieldValues> = {
  onSubmit: SubmitHandler<TFormValues>;
  children: (methods: UseFormReturn<TFormValues>) => React.ReactNode;
  className?: string;
  defaultValues?: DefaultValues<TFormValues>;
  onReady?: (methods: UseFormReturn<TFormValues>) => void;
};

function Form<TFormValues extends FieldValues>(
  {
    children,
    onSubmit,
    className,
    defaultValues,
    onReady,
  }: FormProps<TFormValues>,
  ref: React.Ref<HTMLFormElement>,
) {
  const methods = useForm<TFormValues>({ defaultValues });

  useEffect(() => {
    if (onReady) {
      onReady(methods);
    }
  }, [methods, onReady]);

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        className={className}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        {children(methods)}
      </form>
    </FormProvider>
  );
}

export default forwardRef<HTMLFormElement, FormProps<any>>(Form) as <
  TFormValues extends FieldValues,
>(
  props: FormProps<TFormValues> & { ref?: React.Ref<HTMLFormElement> },
) => ReturnType<typeof Form>;
